import { useState } from 'react';
import { Button, Modal, EmptyState, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useBankAccounts } from '../../../hooks/useFinancialOps';
import {
  useIntercompanyBalance,
  useIntercompanySettlements,
  useSettleIntercompany,
  useReverseSettlement,
} from '../hooks/useIntercompany';
import type { IntercompanyBalance, IntercompanySettlement } from '../services/intercompany.service';
import { formatCurrency } from '../../../utils/format';

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface Props {
  year: number;
}

/**
 * Saldo de rateio entre empresas na competência e a quitação.
 *
 * Não é escopado por empresa de propósito: um saldo envolve sempre duas, então
 * filtrar pela empresa selecionada na página esconderia metade da informação.
 */
export function RateioEntreEmpresasSection({ year }: Props) {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const { data, isLoading } = useIntercompanyBalance(year, month);
  const balances: IntercompanyBalance[] = data?.data ?? [];
  const [settling, setSettling] = useState<IntercompanyBalance | null>(null);

  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-semibold text-ink">Rateio entre empresas</h2>
          <p className="text-xs text-ink-tertiary mt-0.5">
            Gastos que uma empresa pagou pela outra, ainda em aberto nesta competência.
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Competência
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={idx + 1} value={idx + 1}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : balances.length === 0 ? (
          <EmptyState
            title="Nenhum rateio em aberto"
            body={`Não há gastos de uma empresa pagos pela outra em ${MONTH_NAMES[month - 1]}/${year}.`}
          />
        ) : (
          <ul className="divide-y divide-border">
            {balances.map((b) => (
              <li
                key={`${b.fromCompanyId}-${b.toCompanyId}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="text-sm text-ink">
                  <strong>{b.fromCompanyName}</strong> deve{' '}
                  <strong>{formatCurrency(b.amountCents / 100)}</strong> a{' '}
                  <strong>{b.toCompanyName}</strong>
                  <span className="block text-xs text-ink-tertiary mt-0.5">
                    {b.chargeIds.length} {b.chargeIds.length === 1 ? 'gasto' : 'gastos'} — saldo
                    líquido das duas direções
                  </span>
                </div>
                <Button onClick={() => setSettling(b)}>Quitar</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <QuitacoesFeitas year={year} month={month} />

      {settling && (
        <QuitarDialog
          balance={settling}
          year={year}
          month={month}
          onClose={() => setSettling(null)}
        />
      )}
    </section>
  );
}

/**
 * Quitações já feitas na competência. Sem esta lista o par quitado desaparece
 * (o saldo só mostra rateios em aberto) e não há de onde estornar — que é o que
 * destrava desfazer a aprovação de um item de fatura já rateado e quitado.
 */
function QuitacoesFeitas({ year, month }: { year: number; month: number }) {
  const { data } = useIntercompanySettlements(year, month);
  const settlements: IntercompanySettlement[] = data?.data ?? [];
  const reverse = useReverseSettlement();
  const [confirming, setConfirming] = useState<IntercompanySettlement | null>(null);

  if (settlements.length === 0) {
    return null;
  }

  async function handleReverse(s: IntercompanySettlement) {
    try {
      await reverse.mutateAsync(s.id);
      toast.success('Quitação estornada. Os rateios voltaram para em aberto.');
      setConfirming(null);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(e?.response?.data?.message || e?.message || 'Falha ao estornar.');
    }
  }

  return (
    <div className="mt-6 border-t border-border pt-4">
      <h3 className="text-sm font-medium text-ink">Quitações desta competência</h3>
      <ul className="mt-2 divide-y divide-border">
        {settlements.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
            <div className="text-sm text-ink-secondary">
              <strong>{s.fromCompanyName}</strong> transferiu{' '}
              <strong>{formatCurrency(s.amountCents / 100)}</strong> para{' '}
              <strong>{s.toCompanyName}</strong>
              <span className="block text-xs text-ink-tertiary mt-0.5">
                {s.chargeCount} {s.chargeCount === 1 ? 'rateio quitado' : 'rateios quitados'}
              </span>
            </div>
            <Button variant="ghost" onClick={() => setConfirming(s)}>
              Estornar
            </Button>
          </li>
        ))}
      </ul>

      {confirming && (
        <Modal open onClose={() => setConfirming(null)} title="Estornar quitação">
          <div className="space-y-4">
            <p className="text-sm text-ink-secondary">
              Os {confirming.chargeCount} rateios voltam para <strong>em aberto</strong> e as duas
              pernas de caixa desta transferência são apagadas — o caixa das duas empresas volta ao
              que era antes de quitar.
            </p>
            <p className="text-sm text-ink-secondary">
              A transferência que você fez no banco <strong>não</strong> é desfeita por aqui.
            </p>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => setConfirming(null)}>
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={reverse.isPending}
                onClick={() => handleReverse(confirming)}
              >
                {reverse.isPending ? 'Estornando…' : 'Estornar'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function QuitarDialog({
  balance,
  year,
  month,
  onClose,
}: {
  balance: IntercompanyBalance;
  year: number;
  month: number;
  onClose: () => void;
}) {
  const settle = useSettleIntercompany();
  const { data: fromAccountsData } = useBankAccounts(balance.fromCompanyId, { active: true });
  const { data: toAccountsData } = useBankAccounts(balance.toCompanyId, { active: true });
  const fromAccounts = (fromAccountsData?.data ?? []) as Array<{ id: string; name: string }>;
  const toAccounts = (toAccountsData?.data ?? []) as Array<{ id: string; name: string }>;

  const [fromBankAccountId, setFromBankAccountId] = useState('');
  const [toBankAccountId, setToBankAccountId] = useState('');
  const [settlementDate, setSettlementDate] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!fromBankAccountId || !toBankAccountId) {
      toast.error('Selecione a conta de saída e a de entrada.');
      return;
    }
    try {
      await settle.mutateAsync({
        fromCompanyId: balance.fromCompanyId,
        toCompanyId: balance.toCompanyId,
        year,
        month,
        fromBankAccountId,
        toBankAccountId,
        ...(settlementDate ? { settlementDate } : {}),
      });
      toast.success('Rateio quitado.');
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao quitar o rateio.');
    }
  }

  return (
    <Modal open onClose={onClose} title="Quitar rateio entre empresas">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-ink-secondary">
          <strong>{balance.fromCompanyName}</strong> transfere{' '}
          <strong>{formatCurrency(balance.amountCents / 100)}</strong> para{' '}
          <strong>{balance.toCompanyName}</strong>. A transferência real você faz no banco; aqui o
          sistema registra as duas pernas e baixa o saldo.
        </p>

        <div>
          <label className="text-sm text-ink-secondary">
            Conta de saída ({balance.fromCompanyName})
          </label>
          <select
            className="w-full border rounded px-2 py-1"
            value={fromBankAccountId}
            onChange={(e) => setFromBankAccountId(e.target.value)}
          >
            <option value="">Selecione…</option>
            {fromAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm text-ink-secondary">
            Conta de entrada ({balance.toCompanyName})
          </label>
          <select
            className="w-full border rounded px-2 py-1"
            value={toBankAccountId}
            onChange={(e) => setToBankAccountId(e.target.value)}
          >
            <option value="">Selecione…</option>
            {toAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm text-ink-secondary">Data da transferência (opcional)</label>
          <input
            type="date"
            className="w-full border rounded px-2 py-1"
            value={settlementDate}
            onChange={(e) => setSettlementDate(e.target.value)}
          />
          <p className="mt-1 text-xs text-ink-tertiary">
            Em branco usa o último dia da competência, o que faz o mês fechar com o caixa da
            pagadora zerado. Informe a data real se a transferência aconteceu depois.
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={settle.isPending}>
            {settle.isPending ? 'Quitando…' : 'Quitar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
